import type { TemplateEntity } from './templateProfiles.js';

/**
 * Template company 1100 (Legends Hospitality, LLC) in the Legends TRN sandbox: the same entities as
 * the USMF profile, taken from the TRN ledger export of 2026-10-22 (docs/{6A51C3D0-…}.zip, every
 * company; 1100's rows kept). Values are as exported, with record IDs, run-time state (DATETIME,
 * INUSE) and columns blank in every row left out. The retarget rules rename the 1100 in sequence
 * formats (1100-JGE-#########) and voucher series to the new company.
 *
 * - Ledger: chart Shared, structures BS-Corp / P&L-Corp, calendar Fiscal
 * - Journal names: the general-ledger set (daily and periodic GL journals). Left out: AP, AR,
 *   fixed asset, bank reconciliation, ExFlow, project, intercompany and decommissioned journals,
 *   which need their module's setup first. SKS_* columns are the TRN's ISV fields, as exported.
 * - Number sequences: the voucher series those journals use and the 9 GL references (as USMF)
 * - Ledger parameters (the export's one row, exported in 1100) and 1100's 23 main-account overrides
 *
 * Not taken from 1100: the work calendar. 1100's calendar (DFLTCAL) has no working days, and the
 * journal workflow and provisioning use 24hr, so the profile keeps USMF's 24hr row.
 */
export const LEGENDS_1100_ENTITIES: readonly TemplateEntity[] = [
  {
    entityName: 'Ledger',
    targetEntity: 'LedgerEntity',
    columns: [
      'LEGALENTITYID',
      'ACCOUNTINGCURRENCY',
      'ACCOUNTSTRUCTURENAME1',
      'ACCOUNTSTRUCTURENAME2',
      'CHARTOFACCOUNTS',
      'DESCRIPTION',
      'EXCHANGERATETYPE',
      'FISCALCALENDAR',
      'ISBUDGETCONTROLENABLED',
      'MAINACCOUNTIDREALIZEDGAIN',
      'MAINACCOUNTIDREALIZEDLOSS',
      'MAINACCOUNTIDUNREALIZEDGAIN',
      'MAINACCOUNTIDUNREALIZEDLOSS',
      'NAME',
      'REPORTINGCURRENCY',
    ],
    rows: [
      [
        '1100',
        'USD',
        'BS-Corp',
        'P&L-Corp',
        'Shared',
        'Legends Hospitality, LLC',
        'Default',
        'Fiscal',
        'No',
        '835100',
        '835100',
        '835200',
        '835200',
        '1100',
        'USD',
      ],
    ],
  },
  {
    entityName: 'Number sequence code V2',
    targetEntity: 'NumberSequenceTableV2Entity',
    columns: [
      'NUMBERSEQUENCECODE',
      'SCOPETYPE',
      'SCOPEVALUE',
      'CLEANUP',
      'COMPANY',
      'CONTINUOUS',
      'CYCLICAL',
      'FORMAT',
      'INTERVAL',
      'LARGEST',
      'MANUAL',
      'NAME',
      'NEXT',
      'OPERATINGUNITTYPES',
      'PREALLOCATION',
      'QUANTITYOFNUMBERS',
      'SKIPCOUNTING',
      'SMALLEST',
      'STOPPED',
      'TOAHIGHERNUMBER',
      'TOALOWERNUMBER',
    ],
    rows: [
      [
        'Alloc_01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JAL-#########',
        '.000000',
        '999999999',
        'No',
        'Allocation Journal',
        '1',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'BegBal',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-BGBL-########',
        '.000000',
        '99999999',
        'No',
        'Beginning Balance',
        '2040',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'ConsoJrn01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JCN-#########',
        '.000000',
        '999999999',
        'No',
        'Consolidation Journal',
        '2',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Def_Exp',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-DEF-#########',
        '.000000',
        '999999999',
        'No',
        'Deferral Expenses',
        '807',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_1',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_1',
        '182813',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_13',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_13',
        '1',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_15',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_15',
        '1',
        'None',
        'Yes',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_17',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_17',
        '1',
        'None',
        'Yes',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_19',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_19',
        '1',
        'None',
        'Yes',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_21',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_21',
        '47338',
        'None',
        'Yes',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_29',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-#########',
        '.000000',
        '999999999',
        'No',
        'Gene_29',
        '38',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_5',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-####',
        '.000000',
        '3999',
        'No',
        'Gene_5',
        '2000',
        'None',
        'Yes',
        '10',
        'No',
        '2000',
        'No',
        'No',
        'No',
      ],
      [
        'Gene_7',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-######',
        '.000000',
        '999999',
        'No',
        'Gene_7',
        '1',
        'None',
        'Yes',
        '10',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'GenJrn01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JGE-#########',
        '.000000',
        '999999999',
        'No',
        'General Journal',
        '9762',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'NoRecurJrn',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JNR-#########',
        '.000000',
        '999999999',
        'No',
        'Non Recurring Journal',
        '915',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'PayR_Jrn',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JPR-#########',
        '.000000',
        '999999999',
        'No',
        'Payroll Journal',
        '634',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'PerJrn_01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JRC-#########',
        '.000000',
        '999999999',
        'No',
        'Recurring Journal',
        '1',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'RevJrn01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JRV-#########',
        '.000000',
        '999999999',
        'No',
        'Revenue Journal',
        '3',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'TaxJrn01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JTX-#########',
        '.000000',
        '999999999',
        'No',
        'Tax Journal Voucher Series',
        '3',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
      [
        'VenJrn01',
        'DataArea',
        '1100',
        'No',
        '1100',
        'No',
        'No',
        '1100-JVN-#########',
        '.000000',
        '999999999',
        'No',
        'Vendor Entry Journal Voucher Series',
        '4159',
        'None',
        'No',
        '0',
        'No',
        '1',
        'No',
        'No',
        'No',
      ],
    ],
  },
  {
    entityName: 'Number sequence references V2',
    targetEntity: 'NumberSequencesReferenceV2Entity',
    columns: ['SCOPETYPE', 'SCOPEVALUE', 'DATATYPENAME', 'NUMBERSEQUENCECODE', 'REUSENUMBERS'],
    rows: [
      ['DataArea', '1100', 'GeneralJournalEntryJournalNumber', 'Gene_21', 'No'],
      ['DataArea', '1100', 'LedgerAllocationBasisID', 'Gene_17', 'No'],
      ['DataArea', '1100', 'LedgerAllocationRuleID', 'Gene_15', 'No'],
      ['DataArea', '1100', 'LedgerEliminationRuleId', 'Gene_19', 'No'],
      ['DataArea', '1100', 'LedgerExchAdjVoucher', 'Gene_5', 'No'],
      ['DataArea', '1100', 'LedgerJournalId', 'Gene_1', 'No'],
      ['DataArea', '1100', 'LedgerJournalizeNum', 'Gene_7', 'No'],
      ['DataArea', '1100', 'LedgerTempVoucher', 'Gene_13', 'No'],
      ['DataArea', '1100', 'SubledgerJournalNum', 'Gene_29', 'No'],
    ],
  },
  {
    entityName: 'Journal names',
    targetEntity: 'LedgerJournalNameEntity',
    columns: [
      'NAME',
      'AMOUNTSINCLUDESALESTAX',
      'DEFAULTFINANCIALDIMENSIONDISPLAYVALUE',
      'DELETELINESAFTERPOSTING',
      'DESCRIPTION',
      'DETAILLEVEL',
      'FEESPOSTING',
      'HIDESALESTAXFIELDSINJOURNALENTRYFORMS',
      'ISAPPROVALACTIVE',
      'ISFIXEDEXCHANGERATE',
      'ISFIXEDOFFSETACCOUNT',
      'ISWORKFLOWAPPROVALACTIVE',
      'NEWVOUCHER',
      'OFFSETACCOUNTTYPE',
      'OVERRIDESALESTAX',
      'POSTINGLAYER',
      'POSTINGLINELIMIT',
      'PRIVATEFORUSERGROUP',
      'SKS_BR_AUTOPOST',
      'SKS_BTB_BANKTRANSFERJOURNAL',
      'SKS_CCR_AUTOPOST',
      'SKS_CNP_CUSTREIMBURSEMENTJOURNAL',
      'SKS_IHB_PAYMENTREQUESTJOURNAL',
      'TYPE',
      'VOUCHERALLOCATIONATPOSTING',
      'VOUCHERSERIESCODE',
      'VOUCHERSERIESCOMPANYID',
      'WORKFLOWID',
    ],
    rows: [
      [
        'GenJrn',
        'Yes',
        '-------',
        'No',
        'General Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'GenJrn01',
        '1100',
        '001084',
      ],
      [
        'AllocJrn',
        'Yes',
        '',
        'No',
        'Allocation Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'Alloc_01',
        '1100',
        '000471',
      ],
      [
        'BegBal',
        'Yes',
        '-----S0000-D0000-',
        'No',
        'Beginning Balance Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        'Admin',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'BegBal',
        '1100',
        '',
      ],
      [
        'ConsoJrn',
        'Yes',
        '',
        'No',
        'Consolidation Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'ConsoJrn01',
        '1100',
        '000091',
      ],
      [
        'Def_Exp',
        'Yes',
        '',
        'No',
        'Deferral Expenses',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'Def_Exp',
        '1100',
        '',
      ],
      [
        'NoRecurJrn',
        'Yes',
        '',
        'No',
        'Non Recurring Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'NoRecurJrn',
        '1100',
        '001085',
      ],
      [
        'PayrollJrn',
        'Yes',
        '',
        'No',
        'Payroll Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'PayR_Jrn',
        '1100',
        '001086',
      ],
      [
        'RecurJrn',
        'Yes',
        '',
        'No',
        'Recurring Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'No',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Periodic',
        'No',
        'PerJrn_01',
        '1100',
        '',
      ],
      [
        'RevJrn',
        'Yes',
        '',
        'No',
        'Revenue Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'RevJrn01',
        '1100',
        '001087',
      ],
      [
        'TaxJrn',
        'Yes',
        '-------',
        'No',
        'Tax Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'TaxJrn01',
        '1100',
        '001047',
      ],
      [
        'VenJrn',
        'Yes',
        '-------',
        'No',
        'Vendor Entry Journal',
        'Detail',
        'Keep',
        'No',
        'No',
        'No',
        'No',
        'Yes',
        'BalanceSheet',
        'Ledger',
        'No',
        'Current',
        '1000',
        '',
        'No',
        'No',
        'No',
        'No',
        'No',
        'Daily',
        'No',
        'VenJrn01',
        '1100',
        '001085',
      ],
    ],
  },
  {
    entityName: 'Ledger parameters',
    targetEntity: 'LedgerParametersEntity',
    columns: [
      'ALLOWMULTIPLETRANSACTIONSWITHINONEVOUCHER',
      'CREATECLOSINGTRANSACTIONSDURINGFISCALYEARCLOSE',
      'CREATEREVERSALSASCORRECTIONS',
      'DELETEPREVIOUSCLOSINGTRANSACTIONSDURINGFISCALYEARCLOSE',
      'DIMENSIONVALUESUSEDFORSUMMARYACCOUNTOPTION',
      'DONOTUSEERRORACCOUNT',
      'DUPLICATEVOUCHEROPTION',
      'ENABLELEDGERSETTLEMENT',
      'ENABLEREVERSECHARGE',
      'MAXIMUMPENNYDIFFERENCEINACCOUNTINGCURRENCY',
      'MAXIMUMPENNYDIFFERENCEINREPORTINGCURRENCY',
      'ORDERJOURNALIZINGREPORTBYCREATEDDATEANDTIME',
      'PROCESSSOURCEDOCUMENTLINESINPARALLEL',
      'PSNLEDGERINTERESTDISTRIBUTIONENABLE',
      'PSNLEDGERINTERESTDISTRIBUTIONFUNDINGSOURCERULES',
      'PSNLEDGERINTERESTDISTRIBUTIONPOSTALE',
      'PSNPRIORYEARCORRECTION',
      'REQUIRECONTINUOUSNUMBERSEQUENCES',
      'REQUIREJOURNALIZINGONFISCALPERIODCLOSE',
      'REVRECDISABLECONTRACTTERMS',
      'REVRECENABLEREALLOCATION',
      'REVRECHEADERDISCOUNTS',
      'REVRECPOSTTOACCOUNTSRECEIVABLE',
      'SETFISCALYEARTOCLOSEDDURINGFISCALYEARCLOSE',
      'SKIPBUDGETTRANSACTIONSFORFISCALYEARCLOSE',
      'STARTJOURNALIZEREPORTONPAGEONE',
      'USEBUDGETAPPROPRIATION',
      'USEBUDGETRESERVATIONPROCESS',
      'USEENCUMBRANCEPROCESS',
      'USEFUNDDIMENSIONTOCARRYFORWARDPURCHASEORDERS',
      'USEPOSTINGDEFINITIONS',
      'USEPREENCUMBRANCEPROCESS',
      'VOUCHERNUMBERREQUIREDFORFISCALYEARCLOSE',
      'WITHHOLDINGTAXMINIMUMINVOICEAMOUNT',
    ],
    rows: [
      [
        'Yes',
        'No',
        'No',
        'Yes',
        'SourceDocument',
        'No',
        'NoDuplicate',
        'Yes',
        'No',
        '.000000',
        '.000000',
        'No',
        'No',
        'No',
        'DefineFundingRules',
        'Yes',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        'No',
        '.000000',
      ],
    ],
  },
  {
    entityName: 'Main account legal entity overrides',
    targetEntity: 'MainAccountLegalEntityOverrideEntity',
    columns: [
      'MAINACCOUNTID',
      'CHARTOFACCOUNTS',
      'LEGALENTITYID',
      'ACTIVEFROM',
      'ACTIVETO',
      'AUTOALLOCATE',
      'DEFAULTDIMENSIONDISPLAYVALUE',
      'DONOTALLOWMANUALENTRY',
      'FIXEDDIMENSIONSDISPLAYVALUE',
      'ISEXEMPT',
      'ISSUSPENDED',
      'SALESTAXDIRECTION',
      'VALIDATESALESTAX',
    ],
    rows: [
      [
        '201010',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201020',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201030',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201040',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201050',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201100',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201110',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201112',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201114',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201120',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201200',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201300',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201400',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201410',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201420',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '201500',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '-------',
        'No',
        '----Not fixed-Not fixed-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '714900',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '723100',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '732900',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '741900',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '753100',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '762100',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
      [
        '776200',
        'Shared',
        '1100',
        '1900-01-01 00:00:00',
        '1900-01-01 00:00:00',
        'No',
        '----C9900-S1000--',
        'No',
        '-Not fixed-Not fixed-Not fixed-Fixed value-Fixed value-Not fixed-Not fixed',
        'No',
        'No',
        'None',
        'Optional',
      ],
    ],
  },
];
